import { BREAKPOINTS } from '@app/constants/breakpoints';
import { useIsScreenSize } from '@app/utils/hooks';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import React from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '../Button';
import { Search as SearchIcon } from '../Icons';
import { Search } from '../Search';

interface Props {
  title: string;
  totalCount?: number;
  search: string;
  postfix?: React.ReactNode;
  searchPlaceholder?: string;
  onSearchChange: (value: string) => void;
  onSearchBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
}

export const SearchTopBar = ({
  children,
  title,
  totalCount,
  search,
  postfix,
  searchPlaceholder,
  onSearchChange,
  onSearchBlur,
}: React.PropsWithChildren<Props>) => {
  const { t } = useTranslation(['common']);
  const isMobile = useIsScreenSize(BREAKPOINTS.SM);

  const [hasFocus, setHasFocus] = React.useState(false);
  const [shouldExpand, setShouldExpand] = React.useState(!isEmpty(search));

  React.useEffect(() => {
    if (!isEmpty(search)) {
      setShouldExpand(true);
    }
  }, [search]);

  const isFolded = !hasFocus && isEmpty(search);
  const placeholder = searchPlaceholder ?? t('common:search_placeholder');
  const mobileSearchInputRef = React.useRef<HTMLInputElement | null>(null);
  const showExpandedOnMobile = isMobile && shouldExpand;

  const handleFocus = () => {
    setHasFocus(true);
    isMobile && setShouldExpand(true);
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    setHasFocus(false);

    onSearchBlur?.(event);
  };

  const handleSearchButtonClick = () => {
    flushSync(() => {
      setShouldExpand(value => !value);
    });

    mobileSearchInputRef.current?.focus();
  };

  return (
    <div className="sticky top-[--headerHeight] z-topBar mb-2 flex flex-col gap-2 bg-neutral-96 py-2 transition-all group-[.scrollingDown]/body:top-0 lg:mb-0">
      <div className="flex items-center gap-2 sm:gap-8">
        <h2 className="flex-1 heading-3 sm:flex-initial">
          {title}
          {isDefined(totalCount) && <span className="text-neutral-40"> ({totalCount})</span>}
        </h2>
        <div className="flex gap-2 sm:flex-1">{children}</div>
        <div className="flex justify-end gap-2">
          {isMobile ?
            <Button
              icon={SearchIcon}
              size="medium"
              color="secondary"
              variant="bordered"
              onClick={handleSearchButtonClick}
              aria-label={showExpandedOnMobile ? t('common:search_close_button_aria') : t('common:search_open_button_aria')}
            />
          : <Search
              value={search}
              onChange={onSearchChange}
              onBlur={handleBlur}
              onFocus={handleFocus}
              isFolded={isFolded}
              placeholder={placeholder}
          />
          }
          {!isMobile && postfix}
        </div>
      </div>
      {showExpandedOnMobile && (
        <Search ref={mobileSearchInputRef} value={search} placeholder={placeholder} onChange={onSearchChange} onBlur={handleBlur} isFluid />
      )}
    </div>
  );
};
